/**
 * THE COST CAP of a call-eval run (plan §12: "each run has a cost cap: eval runs still charge
 * initiation and residency, and now the call meter, with provider minutes on top").
 *
 * A run spends on two meters, and the cap bounds each, in its own unit:
 * - PROVIDER MINUTES, on the org's own provider key: the call's connected time. Bounded by
 *   `maxCallSeconds`, which the server enforces as the call's maximum length (the eval call
 *   route's `max_call_seconds`), so a runner that dies mid-call still cannot overspend; the runner
 *   also hangs up itself when the time is reached.
 * - WAYAI OPERATIONS, on the org's plan: one per call turn (each delegation the voice raises)
 *   and the call meter's price per connected minute begun. Bounded by `maxOperations`: while the
 *   call is live the runner counts the delegations the provider reports and the minutes begun,
 *   checking before every 10 ms frame of a line and every 20 ms of a wait, and hangs up once the
 *   count reaches the cap, or before a minute begins whose price would pass it. A delegation the voice
 *   raises in the moment of the hang-up can still charge one operation more. After the call, what
 *   the conversation's rows actually charged is read back and reported.
 *
 * A SUITE of runs also has a total, in dollars of provider time (`maxSuiteUsd`): a run starts only
 * when the suite's spend so far plus the run's worst case (its full `maxCallSeconds`) fits.
 */

export interface CostCap {
  /** Longest a run's call may last, in seconds: its provider minutes. */
  maxCallSeconds: number;
  /** Most WayAI operations one run may charge. */
  maxOperations: number;
  /** The provider's price per connected minute, in US dollars (the operator's own rate). */
  providerUsdPerMinute: number;
  /** Most provider dollars the whole suite may spend. */
  maxSuiteUsd: number;
}

/** The defaults `wayai eval call` states before it places a call. */
export const DEFAULT_COST_CAP: CostCap = {
  maxCallSeconds: 180,
  maxOperations: 30,
  providerUsdPerMinute: 0.1,
  maxSuiteUsd: 2,
};

/** What the runner can count of a live call. */
export interface LiveCallCount {
  delegationsSeen: number;
  /**
   * The call's time so far. The runner counts it from before it asks for the call, so it runs
   * ahead of the meter's own clock (which starts when the provider session exists), never behind.
   */
  connectedMs: number;
  /** Operations per connected minute begun; null when the route could not read it. */
  minutePriceOps: number | null;
}

/** Why the cost cap ends a live call. */
export type CapStopReason = 'max_operations' | 'max_call_seconds';

/** What a live call has charged so far, as the runner can count it: the meter's rule (minutes begun × price) plus one per call turn. */
export function liveOperations(input: LiveCallCount): number {
  const minutes = input.connectedMs > 0 ? Math.ceil(input.connectedMs / 60_000) : 0;
  return input.delegationsSeen + minutes * (input.minutePriceOps ?? 0);
}

/**
 * How close to the next connected minute a call ends when that minute's price would not fit. The
 * runner checks before every 10 ms frame of a line and every 20 ms of a wait; its one unchecked
 * gap is the pause before a barge-in (`interruptAfterMs`, 1 s by default), which this covers with
 * room for the hang-up to reach the provider.
 */
export const MINUTE_BOUNDARY_MARGIN_MS = 3_000;

/**
 * Why a live call must end now, or null while it may go on: its length reached, its operations
 * reached, or the next connected minute about to begin when its price would pass the cap (the
 * meter bills every minute begun, so the call ends before it begins). A delegation the voice raises
 * in the moment the runner hangs up can still charge one operation past the cap.
 */
export function liveCapReason(cap: CostCap, input: LiveCallCount): CapStopReason | null {
  if (input.connectedMs >= cap.maxCallSeconds * 1000) return 'max_call_seconds';
  const now = liveOperations(input);
  if (now >= cap.maxOperations) return 'max_operations';
  const price = input.minutePriceOps ?? 0;
  const toNextMinute = 60_000 - (input.connectedMs % 60_000);
  if (price > 0 && input.connectedMs > 0 && now + price > cap.maxOperations && toNextMinute <= MINUTE_BOUNDARY_MARGIN_MS) {
    return 'max_operations';
  }
  return null;
}

/** Provider dollars for a connected span: the provider bills per second. */
export function providerUsd(connectedMs: number, usdPerMinute: number): number {
  return (Math.max(0, connectedMs) / 60_000) * usdPerMinute;
}

/** The suite's running spend, and whether the next run fits. */
export class CostLedger {
  spentUsd = 0;
  spentOperations = 0;

  constructor(readonly cap: CostCap) {}

  /** A run's worst case: its whole allowed length. */
  worstCaseRunUsd(): number {
    return providerUsd(this.cap.maxCallSeconds * 1000, this.cap.providerUsdPerMinute);
  }

  canStartRun(): boolean {
    return this.spentUsd + this.worstCaseRunUsd() <= this.cap.maxSuiteUsd + 1e-9;
  }

  record(run: { providerUsd: number; operations: number }): void {
    this.spentUsd += run.providerUsd;
    this.spentOperations += run.operations;
  }
}
